import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  TDeleteDocumentRequestData,
  TAddDocumentResponse,
  TDeleteManagerDocumentRequestData,
  TAddDocumentManagerRequestData,
  TPreviewDocumentAssetManagerRequest,
  TDocumentRequestInfo,
  TValidateBeforeSigning,
  TUpdateFormResponse,
  TIdentificationForm,
} from '../types';
import api from '~/libs/api';
import { thunkErrorHandler } from '../../utils';
import { buildPreviewHtmlUrl, htmlContent } from '~/helpers/htmlContent';
import { safeWindowOpen } from '~/helpers/safeUrlOpen.ts';
import {
  TBaseRequest,
  TPreviewDocumentRequest,
  TSignatureValidateDocumentResponse,
} from '~/store/types.ts';
import { WEB_DOCS, WebDocName } from '~/constants/docs';
import { AppDispatch, RootState } from '~/store/store';
import { TManagmentStructure } from '~/modules/identification/forms/ManagementStructure/types';
import { TDocumentsInStore } from '~/modules/identification/forms/Documents/types';
import {
  findDocumentById,
  updateOwnershipStructureSchema,
} from '~/modules/identification/forms/OwnershipStructure/utils/utils';
import { generateOwnershipStructurePdf } from '~/modules/identification/forms/OwnershipStructure/utils/documentDiagramUtils';
import { TOwnershipStructure } from '~/modules/identification/forms/OwnershipStructure/types';
import { TGeneralInfoLegalResident } from '~/modules/identification/forms/GeneralInfo/types';
import { TGettingStarted } from '~/modules/identification/forms/GettingStarted/types';
import { IdentificationFormSteps } from '~/modules/identification/types';
import { getDocumentWebBlockByBlockId } from '~/store/identification-form/actions/documentActions.ts';
import { buildUploadFormData } from '~/modules/identification/forms/Documents/utils.ts';
import { UPLOAD_DOCUMENT_URL } from '~/modules/identification/forms/Documents/constants.tsx';

export const addDocument = createAsyncThunk<TAddDocumentResponse, TAddDocumentManagerRequestData>(
  'identification/addDocument',
  async ({ applicationId, managerId, relatedEntityType, data }, { rejectWithValue }) => {
    try {
      const { requestData, blockId } = data;

      const formData = buildUploadFormData({
        requestData,
        appId: applicationId,
        documentWebBlock: getDocumentWebBlockByBlockId(blockId),
        relatedEntityId: managerId,
        relatedEntityType,
      });

      const { data: docInfo } = await api.post(UPLOAD_DOCUMENT_URL, formData);

      return {
        docInfo: { ...docInfo },
        blockId,
        webId: requestData.docType,
        relatedEntityId: managerId,
        relatedEntityType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteDocument = createAsyncThunk<
  { blockId: number; idDoc: number },
  TDeleteDocumentRequestData
>(
  'identification/deleteDocument',
  async ({ applicationId, idDoc, blockId }, { rejectWithValue }) => {
    try {
      await api.delete(`/identification/application/${applicationId}/documents/${idDoc}`);

      return { blockId, idDoc };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteDocumentAssetManager = createAsyncThunk<
  { blockId: number; idDoc: number },
  TDeleteManagerDocumentRequestData
>(
  'identification/deleteAssetManagerDocument',
  async ({ applicationId, idDoc, managerId, blockId }, { rejectWithValue }) => {
    try {
      await api.delete(
        `/identification/asset-managers/${managerId}/screen-forms/application/${applicationId}/documents/${idDoc}`
      );
      return { blockId, idDoc };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const validateDocumentSign = createAsyncThunk<
  TSignatureValidateDocumentResponse,
  { applicationId: string }
>('identification/validateDocumentSign', async ({ applicationId }, { rejectWithValue }) => {
  try {
    const { data } = await api.post(
      `/identification/application/${applicationId}/documents/signature-protocols`,
      null
    );
    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const validateDocumentSignAssetManager = createAsyncThunk<
  TSignatureValidateDocumentResponse,
  { applicationId: string; managerId: number }
>(
  'identification/validateDocumentSignAssetManager',
  async ({ applicationId, managerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/identification/asset-managers/${managerId}/screen-forms/application/${applicationId}/documents/signature-protocols`,
        null
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const previewDocument = createAsyncThunk<void, TPreviewDocumentRequest>(
  'identification/documentPreview',
  async ({ applicationId, idDoc, fileName }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/application/${applicationId}/documents/${idDoc}/preview`
      );
      const fileURL = URL.createObjectURL(data);

      const htmlBlob = new Blob([htmlContent(fileURL, fileName)], { type: 'text/html' });
      const htmlURL = URL.createObjectURL(htmlBlob);
      const previewURL = buildPreviewHtmlUrl(htmlURL, fileName);

      safeWindowOpen(previewURL);

      setTimeout(() => URL.revokeObjectURL(htmlURL), 2000);
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const previewAssetManagerDocument = createAsyncThunk<
  void,
  TPreviewDocumentAssetManagerRequest
>(
  'identification/documentPreviewAssetManager',
  async ({ applicationId, idDoc, fileName, managerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/asset-managers/${managerId}/screen-forms/application/${applicationId}/documents/${idDoc}/preview`
      );
      const fileURL = URL.createObjectURL(data);

      const htmlBlob = new Blob([htmlContent(fileURL, fileName)], { type: 'text/html' });
      const htmlURL = URL.createObjectURL(htmlBlob);
      const previewURL = buildPreviewHtmlUrl(htmlURL, fileName);

      safeWindowOpen(previewURL);

      setTimeout(() => URL.revokeObjectURL(htmlURL), 2000);
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const getSignatureEmail = createAsyncThunk<void, Omit<TDocumentRequestInfo, 'idDoc'>>(
  'identification/documentPreviewAssetManager',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/application/${applicationId}/documents/sending-order`
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteDocumentsInvalidSignatures = createAsyncThunk<null, { applicationId: string }>(
  'identification/deleteDocumentsInvalidSignatures',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/identification/application/${applicationId}/documents/invalid-signatures`
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteDocumentsInvalidSignaturesAssetManager = createAsyncThunk<
  null,
  { applicationId: string; managerId: number }
>(
  'identification/deleteDocumentsInvalidSignaturesAssetManager',
  async ({ applicationId, managerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/identification/asset-managers/${managerId}/screen-forms/application/${applicationId}/documents`
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const regenerateReports = createAsyncThunk<null, { applicationId: string }>(
  'identification/regenerateReports',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/identification/reports/applications/${applicationId}/regenerate-reports`,
        null
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const validateBeforeSigning = createAsyncThunk<TValidateBeforeSigning, TBaseRequest>(
  'identification/validateBeforeSigning',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/application/${applicationId}/validate-before-signing`
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const getDocumentTemplates = createAsyncThunk<
  void,
  { code: string; applicationId: number },
  { rejectValue: { status: number; message: string; errorCode: string | number } }
>('identification/documentTemplates', async ({ code, applicationId }, { rejectWithValue }) => {
  try {
    const baseURL = api.defaults.baseURL ?? '';

    const response = await fetch(
      `${baseURL}/identification/document-templates/${code}/application/${applicationId}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers.get('content-disposition');
    const fileName = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/['"]/g, '').trim()
      : `document-template-${code}`;

    link.setAttribute('download', fileName || '');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const checkRegenerateDocs = createAsyncThunk<
  void,
  {
    docs: TUpdateFormResponse;
    appId: number;
    reportId: IdentificationFormSteps;
    data?: Partial<TIdentificationForm>;
  },
  { state: RootState }
>(
  'identification/checkRegenerateDocs',
  async ({ docs, appId, reportId, data }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState().identification;
      const formData = { ...state.formData, ...(data ?? {}) } as TIdentificationForm;

      const isQuestionnaireGenerated = Object.keys(docs.newDocuments).includes(
        WEB_DOCS[WebDocName.clientQuestionnaire].toString()
      );
      const { mainDocuments } = (formData.documents as TDocumentsInStore)?.documents || {};
      const { isDisabledDiagramGeneration } = state;
      const { isFoundersAndBeneficiariesAbsent, owners, link } =
        (formData.ownershipStructure as TOwnershipStructure) || {};

      const targetDocument = findDocumentById(
        mainDocuments,
        WEB_DOCS[WebDocName.ownershipStructureScheme]
      );
      if (
        (isQuestionnaireGenerated || reportId === IdentificationFormSteps.ownershipStructure) &&
        !isDisabledDiagramGeneration
      ) {
        const { lastName, firstName, middleName, position } =
          (formData.managementStructure as TManagmentStructure) || {};
        const { fullNameUkr } = (formData.generalInfo as TGeneralInfoLegalResident) || {};
        const { identificationNumber } = (formData.gettingStarted as TGettingStarted) || {};
        const clientFullInformation = [position?.label, lastName, firstName, middleName]
          .join(' ')
          .trim();

        const file = await generateOwnershipStructurePdf({
          owners,
          link,
          fullNameUkr,
          identificationNumber: identificationNumber!,
          clientFullInformation,
        });

        await updateOwnershipStructureSchema(
          dispatch as AppDispatch,
          file,
          mainDocuments,
          appId,
          !isFoundersAndBeneficiariesAbsent
        );
      } else if (isDisabledDiagramGeneration && targetDocument?.idDoc) {
        await dispatch(
          deleteDocument({
            applicationId: appId as number,
            idDoc: targetDocument.idDoc,
            blockId: 1,
          })
        );
      }
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const regenerateOwnershipSchemeAfterReports = createAsyncThunk<
  void,
  {
    docs?: TUpdateFormResponse;
    appId: number | string;
    hadSchemeBefore: boolean;
  },
  { state: RootState }
>(
  'identification/regenerateOwnershipSchemeAfterReports',
  async ({ docs, appId, hadSchemeBefore }, { rejectWithValue, dispatch, getState }) => {
    try {
      const isQuestionnaireRegenerated = Object.keys(docs?.newDocuments ?? {}).includes(
        WEB_DOCS[WebDocName.clientQuestionnaire].toString()
      );
      if (!isQuestionnaireRegenerated) return;
      if (!hadSchemeBefore) return;

      const { formData } = getState().identification;
      const { mainDocuments } = (formData.documents as TDocumentsInStore)?.documents || {};
      const { owners, link, isFoundersAndBeneficiariesAbsent } =
        (formData.ownershipStructure as TOwnershipStructure) || {};
      const { lastName, firstName, middleName, position } =
        (formData.managementStructure as TManagmentStructure) || {};
      const { fullNameUkr } = (formData.generalInfo as TGeneralInfoLegalResident) || {};
      const { identificationNumber } = (formData.gettingStarted as TGettingStarted) || {};

      const clientFullInformation = [position?.label, lastName, firstName, middleName]
        .join(' ')
        .trim();

      const previousScheme = findDocumentById(
        mainDocuments,
        WEB_DOCS[WebDocName.ownershipStructureScheme]
      );

      if (previousScheme?.idDoc) {
        await dispatch(
          deleteDocument({
            applicationId: appId,
            idDoc: previousScheme.idDoc,
            blockId: 1,
          })
        ).unwrap();
      }

      const mainDocumentsWithoutScheme = (mainDocuments ?? []).filter(
        (doc) => doc.idDoc !== previousScheme?.idDoc
      );

      const file = await generateOwnershipStructurePdf({
        owners,
        link,
        fullNameUkr,
        identificationNumber: identificationNumber!,
        clientFullInformation,
      });

      await updateOwnershipStructureSchema(
        dispatch as AppDispatch,
        file,
        mainDocumentsWithoutScheme,
        appId,
        !isFoundersAndBeneficiariesAbsent
      );
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
